import { Injectable } from '@angular/core';
import { Group, Profile, UserInfo } from '@tekman/onmat/models';
import { EnvironmentService } from '@tekman/onmat/service/generic';
import { GroupsFacade } from '@tekman/onmat/store/group';
import { ProfileFacade } from '@tekman/onmat/store/profile';
import { StorageService } from '@tekman/shared/service';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { FeaturitService } from 'featurit-sdk-angular';
import { FeaturitEventActivity, FeaturitParams, FeaturitProperties } from '@tekman/shared/models';
import { DefaultFeaturitUserContext } from 'featurit-sdk-js-browser';

@Injectable({
  providedIn: 'root',
})
export class FeaturitWrapperService {
  eventKeys = {
    login: 'login',
    loginFailed: 'login/failed',
    logout: 'logout',
    activityShow: 'activity_show',
    activityDownload: 'activity_download',
    activityPrint: 'activity_print',
    curriculumShow: 'curriculum_show',
    calendarShow: 'calendar_show',
    ludificationInformationShow: 'ludification_information_show',
    ludificationTrackingShow: 'ludification_tracking_show',
    ludificationConfigurationShow: 'ludification_configuration_show',
    ludificationInstructionsShow: 'ludification_instructions_show',
    studentActivityShow: 'student_activity_show',
    activityConfigure: 'activity_configure',
    activityCustomize: 'activity_customize',
    studentActivityEvaluate: 'student_activity_evaluate',
    studentActivityRepeat: 'student_activity_repeat',
    creatorShow: 'creator_show',
    pendingtasksShow: 'pendingtasks_show',
    mygroupShow: 'mygroup_show',
    cooperativeShow: 'cooperative_show',
    evaluationCompetencesShow: 'evaluation_competences_show',
    evaluationActivitiesShow: 'evaluation_activities_show',
    evaluationParametersShow: 'evaluation_parameters_show',
    evaluationExportShow: 'evaluation_export_show',
    evaluationExport: 'evaluation_export',
    activitySave: 'activity_save',
    activitySend: 'activity_send',
    yourtrackingShow: 'yourtracking_show',
    yourratingShow: 'yourrating_show',
    groupSelect: 'group_select',
    configurationCalendarShow: 'configuration_calendar_show',
    configurationGroupStudentsShow: 'configuration_groups-students_show',
    configurationEvaluationShow: 'configuration_evaluation_show',
    itinerarySelect: 'itinerary_select',
    itineraryNextPage: 'itinerary_next_page',
    itineraryImport: 'itinerary_import',
    activityHide: 'activity_hide',
    itinerariesShow: 'itineraries_show',
    contentsShow: 'contents_show',
    monitoringShow: 'monitoring_show',
    resourcesShow: 'resources_show',
    groupsShow: 'groups_show',
    stepsShow: 'step_show',
    resultsShow: 'results_show',
    infoShow: 'info_show',
    teacherGuideShow: 'teacherguide_show',
    teacherGuideOpen: 'teacherguide_open',
    answersShow: 'answers_show',
    answersOpen: 'answers_open',
    defaultSecuence: 'set_default_sequence',
    editSecuence: 'edit_order_sequence',
    createGroupShow: 'create_group_show',
    createGroup: 'create_group',
    stepFinished: 'step_finished',
    stepCurrent: 'step_current',
    stepUnfinished: 'step_unfinished',
    stepHide: 'step_hide',
    stepDisplay: 'step_display',
    projectShow: 'project_activity_show',
    projectHide: 'project_exit',
    mteamsShow: 'mteams_open',
    mteamAdd: 'mteams_add_groups',
    editOrderActivity: 'edit_order_activity',
    chatbotOpen: 'chatbot_open',
    monitoringStudentShow: 'monitoring_student_show',
    monitoringStepGroupShow: 'step_group_show',
    monitoringGroupShow: 'monitoring_group_show',
    monitoringGradesDownload: 'monitoring_grades_download',
    monitoringResultsDownload: 'monitoring_results_download',
    stepCompleted: 'step_completed',
    geogebraError: 'geogebra_error',
  };

  private profile: Profile;
  private userInfo: UserInfo;
  private eventsQueue = [];
  private myRoomId: string;
  private defaultProperties: FeaturitProperties;

  constructor(
    private featurit: FeaturitService,
    private readonly env: EnvironmentService,
    private readonly profileFacade: ProfileFacade,
    private readonly groupFacade: GroupsFacade,
    private readonly localStorage: StorageService
  ) {}

  init(myRoomId: string, frontendApiKey: string): void {
    this.myRoomId = myRoomId;
    this.defaultProperties = new FeaturitProperties();

    combineLatest([this.profileFacade.profile$, this.profileFacade.userInfo$, this.groupFacade.groups$, this.groupFacade.selectedGroup$])
      .pipe(take(1))
      .subscribe(async ([profile, userInfo, groups, selectedGroup]) => {
        this.profile = profile;
        this.userInfo = userInfo;
        const actualGroup = groups.find(group => group.id === selectedGroup.id);
        const roles = Array.isArray(this.profile.roles) ? this.profile.roles : [this.profile.roles];

        this.defaultProperties.course = actualGroup?.courseName;
        this.defaultProperties.distinct_id = this.myRoomId;
        this.defaultProperties.user_role = roles;
        this.defaultProperties.environment = location.origin;
        this.defaultProperties.language = this.localStorage.languageLicense || this.env.settings.i18n.defaultLanguage;
        this.defaultProperties.school_year = this.localStorage.selectedAcademicYear;
        this.defaultProperties.program = 'ONMAT_ZERO';
        this.defaultProperties.group = actualGroup?.name;
        this.defaultProperties.is_demo = this.userInfo.isDemo;

        const myFeaturitUserContext = new DefaultFeaturitUserContext(
          this.myRoomId,
          null,
          null,
          new Map<string, any>([
            ['user_email', this.profile.email],
            ['user_role', roles],
            ['school_id', this.profile.schoolId],
            ['school_name', this.profile.schoolName],
            ['name', this.profile.name],
            ['is_demo', this.userInfo.isDemo],
          ])
        );
        await this.featurit.init({
          tenantIdentifier: 'tekman',
          frontendApiKey: frontendApiKey,
          refreshIntervalMinutes: 5,
          enableAnalytics: false,
          enableTracking: true,
          sendBrowserInfo: true,
          featuritUserContext: myFeaturitUserContext,
        });
        this.featurit.trackPerson();

        while (this.eventsQueue.length) {
          await this.track(this.eventsQueue.shift());
        }
      });
  }

  /**
   * To handle the case when the initial group information is not loaded yet
   * @returns void
   * @memberof FeaturitWrapperService
   */
  updateDefaultProperties(): void {
    //if this.defaultProperties.course means need to update defaultProperties
    combineLatest([this.groupFacade.groups$, this.groupFacade.selectedGroup$])
      .pipe(
        take(1),
        filter((groups, selectedGroup) => groups.length > 0 && !!selectedGroup)
      )
      .subscribe(([groups, selectedGroup]) => {
        const actualGroup = groups.find(group => group.id === selectedGroup.id);
        this.defaultProperties.course = actualGroup?.courseName;
        this.defaultProperties.group = actualGroup?.name;
      });
  }

  track(eventKey: string, properties?: Record<string, any>): Promise<void> {
    this.updateDefaultProperties();
    return new Promise<void>(resolve => {
      if (!this.profile || !this.userInfo) {
        this.eventsQueue.push(eventKey);
        return resolve();
      }
      try {
        Object.assign(properties, this.defaultProperties);
        this.featurit.track(eventKey, properties);
        resolve();
      } catch (error) {
        console.error(error);
        resolve();
      }
    });
  }

  trackEvent(event: FeaturitParams): void {
    this.updateDefaultProperties();

    const trackingProps: FeaturitEventActivity<string> = new FeaturitEventActivity({
      ui_label: event.ui_label,
      ui_object: event.ui_object,
      ui_path: event.ui_path,
      version: event.version,
    });
    this.track(event.eventKey, trackingProps);
  }

  trackHeaderNavigation(eventKey: string): void {
    let label: string;
    switch (eventKey) {
      case this.eventKeys.itinerariesShow: {
        label = 'Secuencia';
        break;
      }
      case this.eventKeys.contentsShow: {
        label = 'Banco de steps';
        break;
      }
      case this.eventKeys.resourcesShow: {
        label = 'Recursos';
        break;
      }
      case this.eventKeys.groupsShow: {
        label = 'Grupos';
        break;
      }
    }
    this.trackEvent({
      ui_object: 'top_menu',
      ui_label: label,
      eventKey: eventKey,
    });
  }

  trackNewGroup(eventKey: string, group: Group): void {
    this.defaultProperties.course = group.courseName;
    this.defaultProperties.group = group.name;
    this.trackEvent({
      ui_object: 'top_menu',
      eventKey: eventKey,
    });
  }
}
